import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import CommonSection from "../components/Ui/CommonSection";
import Helmet from "../components/Helmet/Helmet";
import { Container, Row, Col } from "react-bootstrap";
import "../styles/shop.css";
import { subscribeToProducts, getInitialProducts } from "../services/productService";
import ProductsList from "../components/Ui/ProductsList";
import RecentlyViewed from "../components/Ui/RecentlyViewed";

const MAX_CATALOG_PRICE = 700;
const MIN_CATALOG_PRICE = 85;

const matchesMaterial = (productMaterial, filter) => {
  if (!filter) return true;
  const mat = (productMaterial || "").toLowerCase();
  switch (filter) {
    case "wood":
      return (
        mat.includes("wood") ||
        mat.includes("oak") ||
        mat.includes("ash") ||
        mat.includes("walnut")
      );
    case "boucle":
      return mat.includes("bouclé") || mat.includes("boucle");
    case "linen":
      return mat.includes("linen");
    case "velvet":
      return mat.includes("velvet");
    case "marble":
      return (
        mat.includes("marble") ||
        mat.includes("travertine") ||
        mat.includes("stone")
      );
    case "brass":
      return mat.includes("brass") || mat.includes("metal");
    case "ceramic":
      return (
        mat.includes("ceramic") ||
        mat.includes("stoneware") ||
        mat.includes("earthenware")
      );
    default:
      return true;
  }
};

const materialLabels = {
  wood: "Solid Wood & Oak",
  boucle: "Bouclé Texture",
  linen: "Belgian Linen",
  velvet: "Tailored Velvet",
  marble: "Marble & Travertine",
  brass: "Brushed Brass",
  ceramic: "Ceramic Stoneware",
};

const categoryLabels = {
  sofa: "Sofas & Sectionals",
  chair: "Chairs & Armchairs",
  table: "Dining & Coffee Tables",
  lighting: "Designer Lighting",
  decor: "Home Accents & Decor",
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const [filterCategory, setFilterCategory] = useState(categoryParam || "");
  const [filterMaterial, setFilterMaterial] = useState("");
  const [priceLimit, setPriceLimit] = useState(MAX_CATALOG_PRICE);
  const [sortOrder, setSortOrder] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [allProducts, setAllProducts] = useState(getInitialProducts);

  useEffect(() => {
    const unsubscribe = subscribeToProducts((latest) => {
      setAllProducts(latest);
    });
    return () => unsubscribe();
  }, []);

  // Sync category query param
  useEffect(() => {
    if (categoryParam) {
      setFilterCategory(categoryParam);
    } else {
      setFilterCategory("");
    }
  }, [categoryParam]);

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setFilterCategory(val);
    if (val) {
      setSearchParams({ category: val });
    } else {
      setSearchParams({});
    }
  };

  const handleResetFilters = () => {
    setFilterCategory("");
    setFilterMaterial("");
    setPriceLimit(MAX_CATALOG_PRICE);
    setSortOrder("");
    setSearchTerm("");
    setSearchParams({});
  };

  const hasActiveFilters =
    filterCategory !== "" ||
    filterMaterial !== "" ||
    priceLimit < MAX_CATALOG_PRICE ||
    searchTerm !== "" ||
    sortOrder !== "";

  // Unified reactive filtering & sorting pipeline
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    // 1. Category Filter
    if (filterCategory) {
      result = result.filter((item) => item.category === filterCategory);
    }

    // 2. Material Filter
    if (filterMaterial) {
      result = result.filter((item) =>
        matchesMaterial(item.material, filterMaterial)
      );
    }

    // 3. Price Filter
    if (priceLimit < MAX_CATALOG_PRICE) {
      result = result.filter((item) => item.price <= priceLimit);
    }

    // 4. Search Filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.productName.toLowerCase().includes(term) ||
          (item.material && item.material.toLowerCase().includes(term)) ||
          item.category.toLowerCase().includes(term) ||
          item.shortDesc.toLowerCase().includes(term)
      );
    }

    // 5. Sorting
    if (sortOrder === "low-to-high") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortOrder === "high-to-low") {
      result.sort((a, b) => b.price - a.price);
    } else if (sortOrder === "rating") {
      result.sort((a, b) => (b.avgRating || 0) - (a.avgRating || 0));
    }

    return result;
  }, [allProducts, filterCategory, filterMaterial, priceLimit, searchTerm, sortOrder]);

  return (
    <Helmet title="Shop Collection | L'HABILLEUR">
      <CommonSection title="Atelier Collection" />

      <section className="py-4">
        <Container>
          {/* Main Controls Row */}
          <Row className="shop__controls-row g-3">
            {/* Search Input */}
            <Col lg="4" md="12">
              <div className="search__box mb-0">
                <input
                  type="text"
                  placeholder="Search piece, material, or style..."
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                  aria-label="Search catalog"
                />
                <span>
                  <i className="ri-search-line" />
                </span>
              </div>
            </Col>

            {/* Category Filter */}
            <Col lg="3" md="4" sm="6">
              <div className="filter__widget mb-0">
                <select
                  onChange={handleCategoryChange}
                  value={filterCategory}
                  aria-label="Filter by category"
                >
                  <option value="">All Categories</option>
                  <option value="sofa">Sofas & Sectionals</option>
                  <option value="chair">Chairs & Armchairs</option>
                  <option value="table">Dining & Coffee Tables</option>
                  <option value="lighting">Designer Lighting</option>
                  <option value="decor">Home Accents & Decor</option>
                </select>
              </div>
            </Col>

            {/* Material Filter */}
            <Col lg="3" md="4" sm="6">
              <div className="filter__widget mb-0">
                <select
                  onChange={(e) => setFilterMaterial(e.target.value)}
                  value={filterMaterial}
                  aria-label="Filter by material"
                >
                  <option value="">All Materials & Finishes</option>
                  <option value="wood">Solid Wood & Oak</option>
                  <option value="boucle">Textured Bouclé</option>
                  <option value="linen">Belgian Linen</option>
                  <option value="velvet">Tailored Velvet</option>
                  <option value="marble">Marble & Travertine</option>
                  <option value="brass">Brushed Brass & Metal</option>
                  <option value="ceramic">Ceramic Stoneware</option>
                </select>
              </div>
            </Col>

            {/* Sort Order */}
            <Col lg="2" md="4" sm="12">
              <div className="filter__widget mb-0">
                <select
                  onChange={(e) => setSortOrder(e.target.value)}
                  value={sortOrder}
                  aria-label="Sort products"
                >
                  <option value="">Sort: Featured</option>
                  <option value="low-to-high">Price: Low to High</option>
                  <option value="high-to-low">Price: High to Low</option>
                  <option value="rating">Top Rated</option>
                </select>
              </div>
            </Col>
          </Row>

          {/* Interactive Price Filter Card */}
          <div className="price-filter-card">
            <div className="price-filter__header">
              <span className="price-filter__title">
                <i className="ri-money-dollar-circle-line"></i> Maximum Price Limit
              </span>
              <span className="price-filter__value">
                {priceLimit >= MAX_CATALOG_PRICE ? "All Prices (Up to $700)" : `Up to $${priceLimit}`}
              </span>
            </div>

            <input
              type="range"
              min={MIN_CATALOG_PRICE}
              max={MAX_CATALOG_PRICE}
              step="15"
              value={priceLimit}
              onChange={(e) => setPriceLimit(Number(e.target.value))}
              className="price-filter__slider"
              aria-label="Maximum Price Range"
            />

            <div className="price-filter__presets">
              <span className="text-muted small me-1">Quick Presets:</span>
              <button
                type="button"
                className={`price-preset-btn ${priceLimit >= MAX_CATALOG_PRICE ? "active" : ""}`}
                onClick={() => setPriceLimit(MAX_CATALOG_PRICE)}
              >
                All
              </button>
              <button
                type="button"
                className={`price-preset-btn ${priceLimit === 150 ? "active" : ""}`}
                onClick={() => setPriceLimit(150)}
              >
                Under $150
              </button>
              <button
                type="button"
                className={`price-preset-btn ${priceLimit === 300 ? "active" : ""}`}
                onClick={() => setPriceLimit(300)}
              >
                Under $300
              </button>
              <button
                type="button"
                className={`price-preset-btn ${priceLimit === 450 ? "active" : ""}`}
                onClick={() => setPriceLimit(450)}
              >
                Under $450
              </button>
            </div>
          </div>

          {/* Active Filter Badges */}
          {hasActiveFilters && (
            <div className="active-filters-wrapper">
              <span className="active-filters__label">Active Filters:</span>

              {filterCategory && (
                <span className="filter-chip">
                  Category: {categoryLabels[filterCategory] || filterCategory}
                  <button
                    type="button"
                    onClick={() => {
                      setFilterCategory("");
                      setSearchParams({});
                    }}
                    title="Remove category filter"
                  >
                    ×
                  </button>
                </span>
              )}

              {filterMaterial && (
                <span className="filter-chip">
                  Material: {materialLabels[filterMaterial] || filterMaterial}
                  <button
                    type="button"
                    onClick={() => setFilterMaterial("")}
                    title="Remove material filter"
                  >
                    ×
                  </button>
                </span>
              )}

              {priceLimit < MAX_CATALOG_PRICE && (
                <span className="filter-chip">
                  Price: Up to ${priceLimit}
                  <button
                    type="button"
                    onClick={() => setPriceLimit(MAX_CATALOG_PRICE)}
                    title="Remove price limit"
                  >
                    ×
                  </button>
                </span>
              )}

              {searchTerm && (
                <span className="filter-chip">
                  Search: "{searchTerm}"
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    title="Clear search"
                  >
                    ×
                  </button>
                </span>
              )}

              {sortOrder && (
                <span className="filter-chip">
                  Sort: {sortOrder === "low-to-high" ? "Price Low-High" : sortOrder === "high-to-low" ? "Price High-Low" : "Top Rated"}
                  <button
                    type="button"
                    onClick={() => setSortOrder("")}
                    title="Reset sort"
                  >
                    ×
                  </button>
                </span>
              )}

              <button
                type="button"
                className="filter-chip--clear-all ms-auto"
                onClick={handleResetFilters}
              >
                <i className="ri-refresh-line me-1"></i> Reset All Filters
              </button>
            </div>
          )}

          {/* Results Count Meta */}
          <div className="shop__results-meta">
            <span>
              Showing <strong>{filteredProducts.length}</strong> atelier piece{filteredProducts.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Products Grid */}
          <Row>
            {filteredProducts.length === 0 ? (
              <div className="no-products">
                <i className="ri-search-eye-line fs-2 d-block mb-2 text-muted"></i>
                No pieces found matching your selected criteria.
                <div className="mt-3">
                  <button
                    type="button"
                    className="buy__btn"
                    onClick={handleResetFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            ) : (
              <ProductsList data={filteredProducts} />
            )}
          </Row>

          {/* Recently Viewed Pieces History at Bottom of Catalog */}
          <RecentlyViewed />
        </Container>
      </section>
    </Helmet>
  );
}

export default Shop;
